"use client";

import React from "react";
import { Card, CardContent } from "@ui/card";
import { Badge } from "@ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@ui/accordion";
import {
  Info,
  Scale,
  ShieldCheck,
  Trophy,
  GraduationCap,
  Users,
  HeartHandshake,
  Sparkles,
  CheckCircle2,
  ClipboardList,
  Quote,
} from "lucide-react";

const principles = [
  {
    icon: Scale,
    title: "Based on Genuine Financial Need",
    text: "Select only the category that honestly reflects your circumstances. Choose the level of assistance you truly require — nothing more.",
    accent: "healing",
  },
  {
    icon: ShieldCheck,
    title: "Information May Be Verified",
    text: "OICNM reserves the right to verify your details. Misrepresentation may lead to a scholarship being reduced, withdrawn or revoked.",
    accent: "nature",
  },
  {
    icon: Trophy,
    title: "Highly Competitive",
    text: "Applying does not guarantee an award. Spaces and funding are limited, and applications are assessed competitively.",
    accent: "wisdom",
  },
] as const;

const accentMap = {
  healing: {
    ring: "ring-healing-100",
    iconBg: "bg-gradient-to-br from-healing-500 to-healing-600",
    title: "text-healing-700",
    bar: "bg-healing-500",
  },
  nature: {
    ring: "ring-nature-100",
    iconBg: "bg-gradient-to-br from-nature-500 to-nature-600",
    title: "text-nature-700",
    bar: "bg-nature-500",
  },
  wisdom: {
    ring: "ring-wisdom-100",
    iconBg: "bg-gradient-to-br from-wisdom-500 to-wisdom-600",
    title: "text-wisdom-700",
    bar: "bg-wisdom-500",
  },
} as const;

const sections = [
  {
    id: "financial-need",
    icon: Scale,
    title: "This scholarship is based on genuine financial need",
    body: (
      <>
        <p>
          Before applying, we ask you to be completely truthful with yourself
          and with the College. Please select only the scholarship or
          tuition-support category that genuinely reflects your financial
          circumstances.
        </p>
        <p>
          If you can afford the full tuition, please do not apply for financial
          assistance simply because a scholarship opportunity is available. If
          you can afford a significant portion of the tuition, please apply only
          for the level of assistance you genuinely require.
        </p>
        <p>
          Every scholarship awarded unnecessarily to someone who can afford
          their education may deny another applicant who genuinely cannot afford
          to study without that support.
        </p>
        <p className="font-medium text-healing-700">
          Please allow honesty, integrity and genuine need to guide your
          application.
        </p>
      </>
    ),
  },
  {
    id: "verification",
    icon: ShieldCheck,
    title: "Your financial information may be verified",
    body: (
      <>
        <p>
          OICNM reserves the right to conduct further verification or
          investigation where necessary.
        </p>
        <p>
          If, at any stage, we discover that an applicant deliberately
          misrepresented their financial circumstances, provided false or
          misleading information, or has the reasonable financial capacity to
          pay substantially more than represented in the application, the College
          reserves the right to reconsider the award.
        </p>
        <p>
          This may result in the scholarship being reduced, withdrawn or
          completely revoked, including after an initial scholarship offer has
          been made.
        </p>
        <p className="font-medium text-nature-700">
          Do not choose a scholarship category simply because it provides the
          greatest financial benefit. Choose the category that honestly reflects
          what you genuinely need.
        </p>
      </>
    ),
  },
  {
    id: "competitive",
    icon: Trophy,
    title: "The scholarship is highly competitive",
    body: (
      <>
        <p>
          Please understand that applying does not guarantee that you will
          receive a scholarship. Scholarship spaces and available funding are
          limited, while we may receive applications from many deserving
          individuals.
        </p>
        <p>
          Applications will therefore be considered competitively based on
          factors including financial need, academic and/or professional
          background, quality of application, references, commitment to Natural
          Medicine, suitability for the chosen programme and the overall
          strength of the application.
        </p>
        <p className="font-medium text-gray-800">Following assessment, you may:</p>
        <ol className="space-y-2">
          {[
            "Receive the scholarship category you applied for;",
            "Be offered a different level of scholarship or tuition support;",
            "Be placed on a waiting list; or",
            "Not receive a scholarship offer.",
          ].map((item, i) => (
            <li key={i} className="flex gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-wisdom-100 text-sm font-semibold text-wisdom-700">
                {i + 1}
              </span>
              <span>{item}</span>
            </li>
          ))}
        </ol>
        <p>
          The decision of the Scholarship Committee shall be final. Due to the
          competitive nature and potential volume of applications, OICNM will
          not necessarily provide individual explanations or reasons to
          applicants who are unsuccessful.
        </p>
        <p>
          We therefore encourage every applicant to put genuine effort into
          their application and ensure that all information and supporting
          documents submitted are accurate and complete.
        </p>
      </>
    ),
  },
  {
    id: "admission",
    icon: GraduationCap,
    title: "Admission and scholarship applications",
    body: (
      <>
        <p>
          Receiving a scholarship does not replace the normal admission
          requirements of OICNM. Every scholarship recipient must ultimately
          apply for admission, meet the entry requirements for the chosen
          programme, submit the required documentation and successfully complete
          the admission process.
        </p>
        <p>
          You may apply for admission before or while applying for the
          scholarship. However, we understand that some applicants may prefer to
          know whether they will receive a scholarship before proceeding with
          the admission application.
        </p>
        <p>
          If you genuinely wish to receive a scholarship decision first, you may
          submit your scholarship application before completing your admission
          application. If you are subsequently offered a scholarship, you will
          then be required to complete the admission process before enrolment.
        </p>
        <p className="font-medium text-healing-700">
          Please note that receiving a scholarship offer does not automatically
          guarantee admission into a programme for which you do not meet the
          prescribed entry requirements.
        </p>
      </>
    ),
  },
  {
    id: "who-can-apply",
    icon: Users,
    title: "Who can apply?",
    body: (
      <>
        <p>
          The OICNM Scholarship Programme is open to eligible applicants across
          all courses and programmes offered by the College, subject to meeting
          the applicable admission requirements for each programme.
        </p>
        <p>
          We welcome applications from people of different educational,
          professional and socioeconomic backgrounds.
        </p>
        <p>
          However, particular consideration will be given to genuine financial
          need, merit, suitability, seriousness of purpose, commitment to
          learning and the applicant&apos;s potential to make meaningful use of
          the opportunity.
        </p>
      </>
    ),
  },
] as const;

const checklist = [
  "Do I genuinely need this scholarship?",
  "Have I selected a scholarship category that truthfully reflects what I can and cannot afford?",
  "Is every piece of information I have provided accurate, honest and verifiable?",
  "Have I provided the required documents and references?",
  "If I am given this opportunity, am I genuinely committed to studying, learning and making meaningful use of it?",
];

const ImportantInformation = () => {
  return (
    <section
      id="important-information"
      className="relative overflow-hidden bg-gradient-to-b from-white via-healing-50/40 to-white py-20"
    >
      {/* soft decorative blobs */}
      <div className="pointer-events-none absolute -left-24 top-24 h-72 w-72 rounded-full bg-healing-200/30 blur-3xl" />
      <div className="pointer-events-none absolute -right-24 bottom-24 h-72 w-72 rounded-full bg-wisdom-200/30 blur-3xl" />

      <div className="container relative mx-auto px-4">
        <div className="mx-auto max-w-5xl">
          {/* Header */}
          <div className="mb-14 text-center">
            <Badge className="mb-6 bg-healing-100 px-6 py-2 text-base text-healing-700 shadow-sm">
              <Info className="mr-2 h-5 w-5" />
              Important Information
            </Badge>
            <h2 className="mb-4 text-4xl font-bold text-healing-800 md:text-5xl">
              Please Read Carefully Before Applying
            </h2>
            <p className="mx-auto max-w-2xl text-lg text-gray-600">
              A few honest words about who this scholarship is for — and how to
              give your application its very best chance.
            </p>
          </div>

          {/* Vision / intro feature card */}
          <Card className="mb-14 overflow-hidden border-0 shadow-2xl">
            <div className="grid md:grid-cols-[auto,1fr]">
              <div className="flex items-center justify-center bg-gradient-to-br from-healing-600 to-nature-600 p-8 md:p-10">
                <HeartHandshake className="h-16 w-16 text-white/90" />
              </div>
              <CardContent className="space-y-4 p-8 text-lg leading-relaxed text-gray-700 md:p-10">
                <p>
                  At{" "}
                  <span className="font-semibold text-healing-700">
                    Olive International College of Natural Medicine (OICNM)
                  </span>
                  , we believe that genuine financial limitations should not
                  prevent deserving and passionate individuals from accessing
                  quality education in Natural Medicine.
                </p>
                <p>
                  Our vision extends far beyond awarding certificates. We are
                  committed to redefining Natural Medicine education and practice
                  in Nigeria and beyond, raising a new generation of properly
                  educated, ethical and competent practitioners, and contributing
                  significantly towards the eradication of quackery and poorly
                  trained practice within the profession.
                </p>
                <p>
                  We recognise that there are talented and passionate individuals
                  who genuinely desire this education but simply cannot afford the
                  full tuition. This scholarship programme was created especially
                  to give such individuals an opportunity.
                </p>
              </CardContent>
            </div>
          </Card>

          {/* Guiding principles */}
          <div className="mb-16 grid gap-6 md:grid-cols-3">
            {principles.map(({ icon: Icon, title, text, accent }) => {
              const a = accentMap[accent];
              return (
                <Card
                  key={title}
                  className={`group relative overflow-hidden border-0 shadow-xl ring-1 ${a.ring} transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl`}
                >
                  <span className={`absolute inset-x-0 top-0 h-1.5 ${a.bar}`} />
                  <CardContent className="p-8">
                    <div
                      className={`mb-5 flex h-14 w-14 items-center justify-center rounded-2xl ${a.iconBg} text-white shadow-lg`}
                    >
                      <Icon className="h-7 w-7" />
                    </div>
                    <h3 className={`mb-3 text-xl font-bold ${a.title}`}>
                      {title}
                    </h3>
                    <p className="leading-relaxed text-gray-600">{text}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Detailed sections — accordion for easy navigation */}
          <div className="mb-16">
            <div className="mb-8 flex items-center gap-3">
              <Sparkles className="h-6 w-6 text-nature-600" />
              <h3 className="text-2xl font-bold text-healing-800">
                The full details
              </h3>
            </div>
            <Accordion
              type="single"
              collapsible
              defaultValue={sections[0].id}
              className="w-full space-y-4"
            >
              {sections.map(({ id, icon: Icon, title, body }) => (
                <AccordionItem
                  key={id}
                  value={id}
                  className="group overflow-hidden rounded-2xl border border-healing-100 bg-white shadow-sm transition-all duration-300 hover:border-healing-200 hover:shadow-md data-[state=open]:border-healing-200 data-[state=open]:bg-healing-50/40 data-[state=open]:shadow-lg"
                >
                  <AccordionTrigger className="gap-4 px-5 py-5 text-left hover:no-underline [&>svg]:h-6 [&>svg]:w-6 [&>svg]:shrink-0 [&>svg]:rounded-full [&>svg]:bg-healing-100 [&>svg]:p-1 [&>svg]:text-healing-600 [&>svg]:transition-all [&>svg]:duration-300 group-data-[state=open]:[&>svg]:bg-healing-600 group-data-[state=open]:[&>svg]:text-white">
                    <span className="flex items-center gap-4">
                      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-healing-100 text-healing-700 transition-colors duration-300 group-data-[state=open]:bg-gradient-to-br group-data-[state=open]:from-healing-500 group-data-[state=open]:to-nature-600 group-data-[state=open]:text-white">
                        <Icon className="h-5 w-5" />
                      </span>
                      <span className="text-lg font-semibold text-healing-800">
                        {title}
                      </span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="px-5 pb-6">
                    <div className="space-y-4 border-t border-healing-100 pt-5 text-base leading-relaxed text-gray-600 sm:pl-16">
                      {body}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          {/* Before you submit — checklist */}
          <Card className="mb-14 overflow-hidden border-0 bg-gradient-to-br from-healing-700 to-nature-700 text-white shadow-2xl">
            <CardContent className="p-8 md:p-12">
              <div className="mb-8 flex items-center gap-4">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15 backdrop-blur-sm">
                  <ClipboardList className="h-7 w-7" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold md:text-3xl">
                    Before you submit your application
                  </h3>
                  <p className="text-healing-100">
                    Ask yourself these five questions.
                  </p>
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                {checklist.map((q, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-4 rounded-2xl bg-white/10 p-5 backdrop-blur-sm transition-colors hover:bg-white/15"
                  >
                    <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-nature-200" />
                    <p className="text-base leading-relaxed text-white">{q}</p>
                  </div>
                ))}
              </div>

              <p className="mt-8 text-center text-lg font-medium text-healing-50">
                If your answers are yes, we encourage you to submit your
                application and give it your very best.
              </p>
            </CardContent>
          </Card>

          {/* Closing statement */}
          <div className="relative rounded-3xl border border-healing-100 bg-white p-8 text-center shadow-xl md:p-12">
            <Quote className="mx-auto mb-6 h-10 w-10 text-healing-300" />
            <p className="mx-auto max-w-3xl text-lg leading-relaxed text-gray-700">
              This scholarship is more than financial assistance. It is an
              investment in individuals whom we hope will become part of a new
              generation of knowledgeable, competent, ethical and professionally
              trained Natural Medicine practitioners. Our aim is to make quality
              Natural Medicine education accessible to those who genuinely need
              financial support — without denying truly less-privileged and
              deserving students an opportunity they may otherwise never have.
            </p>

            <div className="my-8 flex flex-col items-center gap-2">
              {["Apply truthfully.", "Apply thoughtfully.", "Choose only the level of assistance you genuinely need.", "And if you are selected, make the opportunity count."].map(
                (line) => (
                  <p
                    key={line}
                    className="text-xl font-semibold text-healing-700 md:text-2xl"
                  >
                    {line}
                  </p>
                ),
              )}
            </div>

            <div className="mx-auto h-px w-24 bg-gradient-to-r from-transparent via-healing-300 to-transparent" />
            <p className="mt-6 text-lg font-bold text-healing-800">
              Olive International College of Natural Medicine (OICNM)
            </p>
            <p className="mt-1 text-sm font-medium uppercase tracking-wide text-nature-600">
              Redefining Natural Medicine Education and Practice
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ImportantInformation;
